import {
  ActivateProfileHelper,
  CreatorProfileInstagram,
  HubCoreService,
  LogService,
  ModalVinculate,
  MySquid,
  User,
  onConfigChange,
  onDefaultLayoutShowFooterChange,
  onDefaultLayoutShowHeaderChange,
  onGeneralLoaderChange,
  onSocialNetworkConnection,
  onUserChange,
  useLinkSocialNetwork,
} from '@hub/shared'
import { SqLoader, SqModalActivation, SqModalWelcomeCreatorsInsights, SqToastHelper } from '@squidit/react-css'
import onModalCreatorsInsightsObservable from '@src/observables/on-modal-creators-insights.observable'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TFunction, useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { SubscriptionLike } from 'rxjs'
import { Header } from './components/header-default/header-default.component'
import { MobileNavigationDefault } from './components/mobile-navigation-default/mobile-navigation-default.component'
import './default.layout.scoped.scss'
import './default.layout.scss'
import { MainMenu } from '@src/components/main-menu/main-menu.component'
import { showInsights } from '@src/utils/showInsights'

export interface menuItem {
  label: string
  path?: string
  href?: string
  icon?: string
  target?: string
  onClick?: () => any
  children?: menuItem[]
  warning?: boolean
  id?: string
  isNew?: boolean
}

export const MenuItemRender = ({ item, t }: { item: menuItem; t: TFunction<'defaultLayout', undefined> }) => {
  return (
    <React.Fragment>
      {item.icon ? <i className={`fa-regular fa-${item.icon} menu-icon`} /> : null}
      <span>
        {t(item.label)}
        {item?.warning ? <i className="fa-solid warning-icon fa-diamond-exclamation" /> : null}
      </span>
      {item.isNew && (
        <span className="new-tag">
          <p className="m-0 p-0">{t('mainMenu.new')}</p>
        </span>
      )}
    </React.Fragment>
  )
}

export default (props: any) => {
  const onConfigChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const onGeneralLoaderChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const onUserChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const onDefaultLayoutShowHeaderChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const onDefaultLayoutShowFooterChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const [configs, setConfig] = useState<MySquid | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [showHeader, setShowHeader] = useState<boolean>(true)
  const [showFooter, setShowFooter] = useState<boolean>(true)
  const [generalLoader, setGeneralLoader] = useState<boolean>(true)
  const logService = useMemo(() => new LogService(), [])
  const [isActivationModalOpen, setIsActivationModalOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const hubCoreService = useMemo(() => new HubCoreService(), [])
  const toast = useMemo(() => new SqToastHelper().toast, [])
  const activateProfileHelper = useMemo(() => new ActivateProfileHelper(), [])
  const [profiles, setProfiles] = useState<CreatorProfileInstagram[]>([])
  const [registerCompleted, setRegisterCompleted] = useState<boolean>(false)
  const { t } = useTranslation('defaultLayout')
  const refAside = useRef<HTMLDivElement>(null)
  const PAGE_SIZE = 10
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState<boolean>(true)
  const [loadingPagination, setLoadingPagination] = useState<boolean>(false)
  const [openMobileMenu, setOpenMobileMenu] = useState(true)

  logService.init('defaultLayout')

  const { open: openModalVinculate, setOpen: setOpenModalVinculate, setSocialNetworkSelected, ...infosModal } = useLinkSocialNetwork({})

  const fetchProfiles = async () => {
    if (loadingPagination || !hasMore) return

    setLoadingPagination(true)

    try {
      hubCoreService.getCreatorProfileBySquidId(user?.squidId || '', false, page, PAGE_SIZE).then((response) => {
        if (response?.length) {
          setProfiles((old) => [...old, ...(response as CreatorProfileInstagram[])])
          setPage(page + 1)
        } else {
          setHasMore(false)
        }
      })
    } catch (error) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      logService.error({ message: 'Error on get creator profile', error })
    } finally {
      setLoadingPagination(false)
    }
  }

  useEffect(() => {
    if (user?.squidId && user?.identities?.length) {
      fetchProfiles()
    }
  }, [user])

  const handleButtonCreatorsInsightsClick = useCallback(async () => {
    if (loading || !user?.squidId) return
    const hasInsights = profiles?.some((profile) => profile.hasCreatorsInsights)
    if (hasInsights) {
      try {
        setLoading(true)
        const response = await hubCoreService.generateToken(user?.squidId)

        if (response && response.link) {
          window.location.href = `${response.link}&origin=${window.location.origin}`
        }
      } catch (error) {
        logService.error({ message: 'Error on generate token', error })
        toast?.error(t('globalMain:errorText'), {
          duration: 3600000,
          position: 'bottom-center',
          closeButton: true,
          className: 'full',
          notOverClick: true,
          immediately: true,
        })
      } finally {
        setLoading(false)
      }
    } else {
      setIsModalOpen(true)
    }
    onModalCreatorsInsightsObservable.next(false)
  }, [hubCoreService, loading, logService, t, user?.squidId, profiles, toast])

  const handleConfirm = useCallback(async () => {
    setLoading(true)

    try {
      if (Array.isArray(profiles) && profiles.length === 0) {
        const tokenResponse = await hubCoreService.generateToken(user?.squidId || '')
        if (tokenResponse && tokenResponse.link) {
          window.location.href = `${tokenResponse.link}&origin=${window.location.origin}`
        }
      } else {
        setIsActivationModalOpen(true)
      }
    } catch (error) {
      logService.error({ message: 'Error on generate token', error })
      toast?.error(t('globalMain:errorText'), {
        duration: 3600000,
        position: 'bottom-center',
        closeButton: true,
        className: 'full',
        notOverClick: true,
        immediately: true,
      })
    } finally {
      setIsModalOpen(false)
      setLoading(false)
    }
  }, [user?.squidId, hubCoreService, profiles])

  const mainMenu: menuItem[] = useMemo(() => {
    const menuItems: menuItem[] = [
      {
        label: 'mainMenu.home',
        path: '/home',
        icon: 'house',
      },
      {
        label: 'mainMenu.pay',
        path: '/payments/info',
        icon: 'wallet',
      },
      {
        label: 'userMenu.payments',
        path: '/payments/data',
        icon: 'money-check-dollar-pen',
      },
      {
        label: 'mainMenu.socialNetworks',
        path: '/profile/social-networks',
        icon: 'link',
      },
      {
        label: 'userMenu.mediakit',
        path: '/mediakit',
        icon: 'photo-film',
      },
      // {
      //   label: 'userMenu.products',
      //   path: '/dashboard/wake-products',
      //   icon: 'bag-shopping',
      // },
    ]

    // TODO: Validação Meta remover
    if (showInsights({ configs, user })) {
      menuItems.push({
        label: 'mainMenu.creatorsInsights',
        icon: 'chart-user',
        onClick: () => handleButtonCreatorsInsightsClick(),
        isNew: true,
      })
    }

    return menuItems
    // TODO: Validação Meta remover
  }, [handleButtonCreatorsInsightsClick, configs, user?.profileadditionalinfo?.email])

  const campaignMenu: menuItem[] = [
    {
      label: 'campaignMenu.general.all',
      path: '/campaigns',
      icon: 'bullhorn',
    },
    {
      label: 'campaignMenu.mine.signedUp',
      path: '/campaigns/participating',
      icon: 'camera',
    },
    {
      label: 'campaignMenu.mine.sent',
      path: '/campaigns/sent',
      icon: 'bars-progress',
    },
    {
      label: 'campaignMenu.mine.finished',
      path: '/campaigns/finished',
      icon: 'flag-checkered',
    },
  ]

  const handleClick = (item: menuItem, e: any) => {
    e.preventDefault()

    if (item.onClick && typeof item.onClick === 'function') {
      item.onClick()
    }
  }

  const renderMenu = (menu: menuItem[], customItens?: React.Component | JSX.Element) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { t } = useTranslation('defaultLayout')

    if (menu?.length) {
      return (
        <ul>
          <>
            {menu?.map((item: menuItem, key: number) => {
              if (item.children) {
                return (
                  <li key={key}>
                    <h6>{t(item.label)}</h6>
                    {renderMenu(item.children)}
                  </li>
                )
              }
              if (item.href) {
                return (
                  <li key={key}>
                    <a href={item.href} target={item.target} id={item.id}>
                      <MenuItemRender item={item} t={t} />
                    </a>
                  </li>
                )
              }
              if (item.path) {
                return (
                  <li key={key}>
                    <NavLink end className={({ isActive }) => (isActive ? 'active' : '')} to={item.path} id={item.id}>
                      <MenuItemRender item={item} t={t} />
                      <i className="fa-regular fa-chevron-right arrow-icon" />
                    </NavLink>
                  </li>
                )
              }
              if (item.onClick && typeof item.onClick === 'function') {
                return (
                  <li key={key}>
                    <button
                      id={item.id}
                      type="button"
                      onClick={(e) => {
                        handleClick(item, e)
                      }}
                      title={t(item.label)}
                    >
                      <MenuItemRender item={item} t={t} />
                      <i className="fa-regular fa-chevron-right arrow-icon" />
                    </button>
                  </li>
                )
              }
            })}
            {customItens}
          </>
        </ul>
      )
    }
    return <span />
  }

  useEffect(() => {
    onConfigChangeSubscription.current = onConfigChange.subscribe((configChanged: MySquid | any) => {
      if (configChanged?._id) {
        setConfig(configChanged)
      }
    })
    onGeneralLoaderChangeSubscription.current = onGeneralLoaderChange.subscribe((generalLoader: boolean) => {
      setGeneralLoader(generalLoader)
    })
    onUserChangeSubscription.current = onUserChange.subscribe(async (user: any) => {
      setUser(user)
      if (!user?.squidId) {
        setRegisterCompleted(false)
        return
      }
      setRegisterCompleted((await hubCoreService.getRegisterCompleteness(user?.squidId))?.completed)
    })
    onDefaultLayoutShowFooterChangeSubscription.current = onDefaultLayoutShowFooterChange.subscribe((showFooter: boolean) => {
      setShowFooter(showFooter)
    })
    onDefaultLayoutShowHeaderChangeSubscription.current = onDefaultLayoutShowHeaderChange.subscribe((showHeader: boolean) => {
      setShowHeader(showHeader)
    })
    return () => {
      onConfigChangeSubscription.current?.unsubscribe()
      onGeneralLoaderChangeSubscription.current?.unsubscribe()
      onUserChangeSubscription.current?.unsubscribe()
      onDefaultLayoutShowFooterChangeSubscription.current?.unsubscribe()
      onDefaultLayoutShowHeaderChangeSubscription.current?.unsubscribe()
    }
  }, [
    onGeneralLoaderChangeSubscription,
    onConfigChangeSubscription,
    onUserChangeSubscription,
    onDefaultLayoutShowFooterChangeSubscription,
    onDefaultLayoutShowHeaderChangeSubscription,
  ])

  useEffect(() => {
    const onSocialNetworkConnectionObservable: SubscriptionLike = onSocialNetworkConnection?.subscribe((socialNetworkConnection) => {
      if (socialNetworkConnection?.connect) {
        if (socialNetworkConnection?.socialNetwork) {
          setSocialNetworkSelected(
            socialNetworkConnection?.socialNetwork === 'instagram' ? 'facebook' : socialNetworkConnection?.socialNetwork
          )
        }
        setOpenModalVinculate(openModalVinculate)
      }
    })

    return () => {
      onSocialNetworkConnectionObservable?.unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const onModalCreatorsInsightsSubscription = onModalCreatorsInsightsObservable?.subscribe((open) => {
      if (open) {
        handleButtonCreatorsInsightsClick()
      }
    })

    return () => onModalCreatorsInsightsSubscription?.unsubscribe()
  }, [handleButtonCreatorsInsightsClick])

  const handleToggleActivation = useCallback(
    async (profileId: string, socialNetwork: 'instagram' | 'tiktok' | 'youtube' | 'twitter', currentState: boolean) => {
      try {
        await activateProfileHelper?.activateProfileForCreatorsInsights({ profileId, currentState, socialNetwork })
        const profilesUpdated = profiles?.map((profile) =>
          profile.profileId === profileId ? { ...profile, ...{ hasCreatorsInsights: !currentState } } : profile
        )
        setProfiles(profilesUpdated)
      } catch (error: any) {
        logService.error({ message: 'Error on toggle creator insights', error })
        toast?.error(t('globalMain:errorText'), {
          duration: 3600000,
          position: 'bottom-center',
          closeButton: true,
          className: 'full',
          notOverClick: true,
          immediately: true,
        })
      }
    },
    [activateProfileHelper, logService, t, toast, profiles]
  )

  const handleTogglePublicProfile = useCallback(
    async (profileId: string, socialNetwork: 'instagram' | 'tiktok' | 'youtube' | 'twitter', currentState: boolean) => {
      try {
        await activateProfileHelper?.activateProfileForPublicView({ profileId, currentState, socialNetwork })
        const profilesUpdated = profiles?.map((profile) =>
          profile.profileId === profileId ? { ...profile, ...{ isSharedCreatorsInsights: !currentState } } : profile
        )
        setProfiles(profilesUpdated)
      } catch (error: any) {
        logService.error({ message: 'Error on toggle creator insights', error })
        toast?.error(t('globalMain:errorText'), {
          duration: 3600000,
          position: 'bottom-center',
          closeButton: true,
          className: 'full',
          notOverClick: true,
          immediately: true,
        })
      }
    },
    [activateProfileHelper, logService, t, toast, profiles]
  )

  return (
    <div>
      {generalLoader || !user ? (
        <div className="main-loader">
          <SqLoader size="bigger" />
        </div>
      ) : null}

      <div className={`default-layout ${generalLoader || !user ? 'display-none' : ''}`}>
        <Header className={!showHeader || props.loading ? 'display-none' : ''} />
        <main className="container new-container">
          <aside ref={refAside}>
            <nav className={`${props.loading ? 'display-none' : ''} nav-wrapper`}>
              <MainMenu open={openMobileMenu} setOpen={setOpenMobileMenu} mode="desktop" />
            </nav>
          </aside>
          <div className="content">{props.children}</div>
        </main>
        <MobileNavigationDefault className={!showFooter || props.loading ? 'display-none' : ''} configs={configs} />
      </div>
      {user ? <ModalVinculate {...infosModal} /> : null}
      {isModalOpen ? (
        <SqModalWelcomeCreatorsInsights
          open={isModalOpen}
          modalSize="xl"
          buttonClose={true}
          onCloseChange={() => setIsModalOpen(false)}
          onOpenChange={(open: boolean) => setIsModalOpen(open)}
          forceMobileNoMargin={true}
          onConfirm={() => handleConfirm()}
          loading={loading}
        />
      ) : null}
      <SqModalActivation
        open={isActivationModalOpen}
        requireActiveProfile={true}
        onConfirm={() => handleButtonCreatorsInsightsClick()}
        onToggleCreatorsInsights={handleToggleActivation}
        onTogglePublicProfile={handleTogglePublicProfile}
        onLoadMore={fetchProfiles}
        loading={loadingPagination}
        hasMore={hasMore}
        profiles={
          profiles?.map((profile) => ({
            profileId: profile.profileId,
            socialNetwork: profile.socialNetwork,
            username: profile.username,
            hasCreatorsInsights: profile.hasCreatorsInsights ?? false,
            picture: profile.picture,
            followers: profile.followers,
            hasSocialNetworkCache: profile.hasSocialNetworkCache,
            isSharedCreatorsInsights: profile.isSharedCreatorsInsights ?? false,
            hasValidToken: profile.hasValidToken,
          })) ?? []
        }
        onOpenChange={(open: boolean) => setIsActivationModalOpen(open)}
        titleModal={t('modalActivation.activationQuestion')}
        messageModal={t('modalActivation.insightsText')}
        textButton={t('modalActivation.buttonDone')}
        registerCompleted={registerCompleted}
      />
    </div>
  )
}
